'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var React = require('react');
var nthHookTranslations = require('@narvar/nth-hook-translations');
var index = require('../../providers/ClaimBuilder/index.js');
var SessionContext = require('../../providers/SessionContext.js');
var useAnalytics = require('../../providers/useAnalytics.js');
var useTitanAnalytics = require('../../providers/titan/useTitanAnalytics.js');
var ItemAdditionalInfoProvider = require('./ItemAdditionalInfoProvider.js');

function _interopDefaultLegacy (e) { return e && typeof e === 'object' && 'default' in e ? e : { 'default': e }; }

var React__default = /*#__PURE__*/_interopDefaultLegacy(React);

const ItemAdditionalInfoViewRoot = _ref => {
  let {
    children
  } = _ref;
  const {
    state: {
      claimType
    }
  } = index.useClaimBuilder();
  const titan = useTitanAnalytics["default"]();
  const logEvent = useAnalytics["default"]();
  const {
    orderNumber
  } = SessionContext.useSession();
  const {
    itemDetails,
    claimDetails
  } = ItemAdditionalInfoProvider.useItemAdditionalInfo();
  React.useEffect(() => {
    // Note: This effect is only called on first component render
    const claimSteps = claimDetails.claimSteps.map(claimStep => claimStep.id) ?? [];
    titan === null || titan === void 0 ? void 0 : titan.batch({
      batch: [{
        type: 'page',
        name: 'Item Additional Info'
      }, {
        type: 'track',
        event: 'product_viewed',
        properties: {
          product_id: itemDetails.itemId,
          sku: itemDetails.sku,
          name: itemDetails.name,
          extra_properties: [{
            key: 'claim_steps',
            value: claimSteps.join(',')
          }, {
            key: 'current_claim_step',
            value: index.ClaimStepId.ADDITIONAL_INFO
          }]
        }
      }]
    })
    // eslint-disable-next-line no-console
    .catch(e => console.error(e));
    logEvent({
      event_type: useAnalytics.ClaimsEventTypes.PAGE_LOAD,
      event_component: useAnalytics.ClaimsEventComponents.NTH_KIT_CLAIMS_ITEM_ADDITIONAL_INFO,
      page_step: useAnalytics.ClaimsPageStep.SELECTION_COMMENT,
      order_number: orderNumber,
      claim_reason: claimType
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return /*#__PURE__*/React__default["default"].createElement(React__default["default"].Fragment, null, children);
};
const ItemAdditionalInfoGuarded = _ref2 => {
  let {
    children
  } = _ref2;
  const {
    state: {
      currentItemClaim,
      claimType
    },
    getFullItemDetails,
    handleInvalidData
  } = index.useClaimBuilder();
  const {
    claimsAdditionalInfoCommentsHelperTextRequiredWrongItem,
    claimsAdditionalInfoCommentsHelperTextRequiredItemMissing,
    claimsAdditionalInfoCommentsHelperTextRequiredItemDamaged,
    claimsAdditionalInfoCommentsHelperTextOptionalWrongItem,
    claimsAdditionalInfoCommentsHelperTextOptionalItemMissing,
    claimsAdditionalInfoCommentsHelperTextOptionalItemDamaged,
    claimsAdditionalInfoConfirmationLabelWrongItem,
    claimsAdditionalInfoConfirmationLabelItemDamaged,
    claimsAdditionalInfoConfirmationLabelItemMissing,
    claimsAdditionalInfoCommentsLabelWrongItem,
    claimsAdditionalInfoCommentsLabelItemDamaged,
    claimsAdditionalInfoCommentsLabelItemMissing
  } = nthHookTranslations.useTranslations();
  const currentItemDetails = currentItemClaim !== null && currentItemClaim !== void 0 && currentItemClaim.uid ? getFullItemDetails(currentItemClaim === null || currentItemClaim === void 0 ? void 0 : currentItemClaim.uid, currentItemClaim === null || currentItemClaim === void 0 ? void 0 : currentItemClaim.packageId) : null;

  // Ideally we'd get the claimStep from the currentItemClaim, however that value is stored
  // in ClaimBuilder, and when navigating from step to step, the currentItemClaim can be temporarily
  // incorrect. When the claim builder currentItemClaim step updates, the view re-renders with
  // the next steps data, and then on the next render navigation happens. In order to most completely
  // solve this, we should bring the currentItemClaim out of claim builder and closer to the
  // quantity / photos / additional info views, and make the data for that view be more dependent on
  // view parameters.
  const additionalInfoClaimStep = currentItemClaim === null || currentItemClaim === void 0 ? void 0 : currentItemClaim.claimSteps.find(claimStep => claimStep.id === index.ClaimStepId.ADDITIONAL_INFO);
  if (!currentItemDetails || !currentItemClaim || !additionalInfoClaimStep || !index.isItemClaimType(claimType)) {
    handleInvalidData();
    return null;
  }
  return /*#__PURE__*/React__default["default"].createElement(ItemAdditionalInfoProvider.ItemAdditionalInfoProvider, {
    value: {
      currentClaimStep: additionalInfoClaimStep,
      currentItemClaim,
      currentItemDetails,
      claimType,
      translationsMap: {
        WrongItem: {
          helperTextOptional: claimsAdditionalInfoCommentsHelperTextOptionalWrongItem ?? '',
          helperTextRequired: claimsAdditionalInfoCommentsHelperTextRequiredWrongItem ?? '',
          confirmationLabel: claimsAdditionalInfoConfirmationLabelWrongItem ?? '',
          commentLabel: claimsAdditionalInfoCommentsLabelWrongItem ?? ''
        },
        ItemMissing: {
          helperTextOptional: claimsAdditionalInfoCommentsHelperTextOptionalItemMissing ?? '',
          helperTextRequired: claimsAdditionalInfoCommentsHelperTextRequiredItemMissing ?? '',
          confirmationLabel: claimsAdditionalInfoConfirmationLabelItemMissing ?? '',
          commentLabel: claimsAdditionalInfoCommentsLabelItemMissing ?? ''
        },
        ItemDamaged: {
          helperTextOptional: claimsAdditionalInfoCommentsHelperTextOptionalItemDamaged ?? '',
          helperTextRequired: claimsAdditionalInfoCommentsHelperTextRequiredItemDamaged ?? '',
          confirmationLabel: claimsAdditionalInfoConfirmationLabelItemDamaged ?? '',
          commentLabel: claimsAdditionalInfoCommentsLabelItemDamaged ?? ''
        }
      }
    }
  }, /*#__PURE__*/React__default["default"].createElement(ItemAdditionalInfoViewRoot, null, children));
};
var ItemAdditionalInfoViewRoot$1 = nthHookTranslations.withTranslationsProvider(ItemAdditionalInfoGuarded, {
  translationKeys: ['claimsAdditionalInfoCommentsHelperTextRequiredWrongItem', 'claimsAdditionalInfoCommentsHelperTextRequiredItemMissing', 'claimsAdditionalInfoCommentsHelperTextRequiredItemDamaged', 'claimsAdditionalInfoCommentsHelperTextOptionalWrongItem', 'claimsAdditionalInfoCommentsHelperTextOptionalItemMissing', 'claimsAdditionalInfoCommentsHelperTextOptionalItemDamaged', 'claimsAdditionalInfoConfirmationLabelWrongItem', 'claimsAdditionalInfoConfirmationLabelItemDamaged', 'claimsAdditionalInfoConfirmationLabelItemMissing', 'claimsAdditionalInfoCommentsLabelWrongItem', 'claimsAdditionalInfoCommentsLabelItemDamaged', 'claimsAdditionalInfoCommentsLabelItemMissing']
});

exports["default"] = ItemAdditionalInfoViewRoot$1;
